import * as React from 'react'
import { FontWeights, mergeStyleSets, Stack, Text } from '@fluentui/react'
import { IFileDto, IOverviewItemDto } from '../../config/fileConfig'
import { formatFullDate } from '../../helpers/dateHelper'
import IconValue from '../IconValue/IconValue'
import Loading from '../../components/Loading'
import MoneyText from '../Text/Currency/MoneyText'
import useCurrency from '../../hooks/useCurrency'
import useModules from '../../hooks/module/useModules'
import { ModuleType } from '../../context/GlobalContext'
import useRoles, { Role } from '../../hooks/useRoles'
import { isAppContext } from '../../helpers/appHelper'
import { AppContext } from '../../config/appConfig'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

type Props = {
    file?: IOverviewItemDto | IFileDto
    showTitle?: boolean
    showDescription?: boolean
    className?: string
}

const classNames = mergeStyleSets({
    title: { fontWeight: FontWeights.regular },
    itemWrap: { width: '100%' },
})

export default function FileHeader(props: Props): React.ReactElement {
    const { t: translate } = useTranslation()

    const modules = useModules()
    const roles = useRoles()
    const { currency, currencyName } = useCurrency(props.file?.currency)

    const copyReferenceClick = () => {
        if (isAppContext([AppContext.Compose])) {
            Office.context.mailbox.item?.subject?.getAsync(result => {
                if (result.value.toLowerCase().indexOf(props.file.reference.toLowerCase()) === -1) {
                    Office.context.mailbox.item?.subject.setAsync(`${result.value} [${props.file.reference}]`)
                }
            })
        } else {
            navigator.clipboard.writeText(`[${props.file.reference}]`)
            toast(translate('file.reference.copied'), { autoClose: 1000 })
        }
    }

    if (!props.file) {
        return <Loading />
    }

    return (
        <Stack horizontal wrap className={props.className} tokens={{ childrenGap: 's1 l2' }}>
            {props.showTitle && (
                <Text
                    variant="xLarge"
                    className={`${classNames.title} ${classNames.itemWrap}`}
                    onClick={() => copyReferenceClick()}
                >
                    {props.file.reference}: {props.file.name}
                </Text>
            )}
            {props.showDescription && (props.file as IFileDto).description && (
                <IconValue textVariant="mediumPlus" iconName="TextDocument" wrap>
                    {(props.file as IFileDto).description}
                </IconValue>
            )}
            {props.file.owner?.fullName && (
                <IconValue textVariant="mediumPlus" iconName="Contact">
                    {props.file.owner?.fullName}
                </IconValue>
            )}
            {modules.has(ModuleType.Invoices) && roles.none([Role.FileLimited]) && (
                <IconValue
                    textVariant="mediumPlus"
                    iconName="Money"
                    value={
                        props.file.rate ? (
                            <MoneyText amount={props.file.rate} currency={currency} />
                        ) : (
                            `${translate('file.rate.tiers.label')}, ${currencyName} (${currency.symbol})`
                        )
                    }
                />
            )}
            {(props.file as IFileDto).dateOfIncident && (
                <IconValue textVariant="mediumPlus" iconName="Calendar">
                    {formatFullDate((props.file as IFileDto).dateOfIncident)}
                </IconValue>
            )}
        </Stack>
    )
}
